import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, doc, setDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

function LabRecruitLanding() {
    const navigate = useNavigate();
    const goSignup = () => navigate('/signup?type=lab');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({ labName: '', phone: '', email: '' });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const openModal = () => {
        setErrorMsg('');
        setSubmitted(false);
        setShowModal(true);
    };
    const closeModal = () => {
        setShowModal(false);
        setForm({ labName: '', phone: '', email: '' });
        setSubmitted(false);
        setErrorMsg('');
    };
    const handleSubmit = async () => {
        setErrorMsg('');
        if (!form.labName.trim()) { setErrorMsg('기공소 이름을 입력해주세요.'); return; }
        if (!form.phone.trim()) { setErrorMsg('전화번호를 입력해주세요.'); return; }
        if (!form.email.trim()) { setErrorMsg('이메일을 입력해주세요.'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setErrorMsg('올바른 이메일 형식이 아닙니다.'); return; }
        setSubmitting(true);
        try {
            const params = new URLSearchParams(window.location.search);
            const utmSource = params.get('utm_source') || 'direct';
            const utmCampaign = params.get('utm_campaign') || '';
            await addDoc(collection(db, 'freeTrialRequests'), {
                source: 'landing-lab-recruit',
                utmSource,
                utmCampaign,
                status: 'pending',
                businessType: 'lab',
                companyName: form.labName.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            setSubmitted(true);
        } catch (e) {
            console.error('신청 실패:', e);
            setErrorMsg('신청 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 랜딩 방문 기록 (세션당 1회, UTM 포함)
    useEffect(() => {
        const logVisit = async () => {
            try {
                const sessionKey = 'promo_lab_recruit_visited';
                if (sessionStorage.getItem(sessionKey)) return;
                sessionStorage.setItem(sessionKey, '1');
                const now = new Date();
                const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const params = new URLSearchParams(window.location.search);
                const utmSource = (params.get('utm_source') || '').substring(0, 80);
                const utmMedium = (params.get('utm_medium') || '').substring(0, 80);
                const utmCampaign = (params.get('utm_campaign') || '').substring(0, 120);
                const utmTerm = (params.get('utm_term') || '').substring(0, 120);
                const utmContent = (params.get('utm_content') || '').substring(0, 120);
                const sourceKey = utmSource || 'direct';
                await addDoc(collection(db, 'promoVisits'), {
                    page: 'lab-recruit',
                    date: dateStr,
                    utmSource, utmMedium, utmCampaign, utmTerm, utmContent,
                    source: sourceKey,
                    userAgent: (navigator.userAgent || '').substring(0, 300),
                    referrer: (document.referrer || '').substring(0, 300),
                    language: navigator.language || '',
                    createdAt: serverTimestamp(),
                });
                await setDoc(
                    doc(db, 'promoStats', `lab-recruit_${dateStr}`),
                    { page: 'lab-recruit', date: dateStr, visits: increment(1), updatedAt: serverTimestamp() },
                    { merge: true }
                );
                await setDoc(
                    doc(db, 'promoStats', 'lab-recruit_total'),
                    { page: 'lab-recruit', visits: increment(1), updatedAt: serverTimestamp() },
                    { merge: true }
                );
                // 매체별 집계
                await setDoc(
                    doc(db, 'promoStats', `lab-recruit_src_${sourceKey}`),
                    { page: 'lab-recruit', source: sourceKey, visits: increment(1), updatedAt: serverTimestamp() },
                    { merge: true }
                );
                await setDoc(
                    doc(db, 'promoStats', `lab-recruit_${dateStr}_src_${sourceKey}`),
                    { page: 'lab-recruit', date: dateStr, source: sourceKey, visits: increment(1), updatedAt: serverTimestamp() },
                    { merge: true }
                );
            } catch (e) { /* silent */ }
        };
        logVisit();
    }, []);

    const signupUrl = 'https://www.dentalcluster.com/signup';
    const findLabUrl = 'https://www.dentalcluster.com/find-lab';

    const S = {
        body: { fontFamily: "'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif", background: '#0f172a', color: '#fff', minHeight: '100vh' },
        header: { padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, background: 'rgba(15,23,42,0.95)', backdropFilter: 'blur(10px)', zIndex: 100 },
        headerInner: { maxWidth: '900px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        logoRow: { display: 'flex', alignItems: 'center', gap: '8px' },
        logoIcon: { width: '30px', height: '30px', background: '#6366f1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: '11px' },
        logoText: { fontSize: '14px', fontWeight: 800 },
        headerBtn: { padding: '8px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', textDecoration: 'none' },
        headerBtnSecondary: { padding: '8px 14px', background: 'transparent', color: '#a5b4fc', border: '1px solid rgba(165,180,252,0.35)', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', textDecoration: 'none' },
        heroCtaRow: { display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' },
        heroCtaSecondary: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '16px 32px', background: 'transparent', color: '#a5b4fc', border: '1.5px solid rgba(165,180,252,0.4)', borderRadius: '14px', fontSize: isMobile ? '15px' : '17px', fontWeight: 800, cursor: 'pointer', textDecoration: 'none' },
        ctaBtnSecondary: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '16px 32px', background: 'transparent', color: '#a5b4fc', border: '1.5px solid rgba(165,180,252,0.4)', borderRadius: '14px', fontSize: '17px', fontWeight: 800, cursor: 'pointer', textDecoration: 'none' },
        priceContrastBox: { marginTop: '28px', display: 'inline-flex', alignItems: 'stretch', gap: 0, borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '560px', width: '100%' },
        priceLeft: { flex: 1, minWidth: '200px', padding: isMobile ? '20px 18px' : '24px 28px', backgroundColor: 'rgba(99,102,241,0.08)', textAlign: 'center' },
        priceArrow: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '0 4px' : '0 8px', background: 'rgba(0,0,0,0.2)' },
        priceRight: { flex: 1, minWidth: '200px', padding: isMobile ? '20px 18px' : '24px 28px', backgroundColor: 'rgba(245,196,81,0.12)', textAlign: 'center' },
        container: { maxWidth: '900px', margin: '0 auto', padding: '0 20px' },
        hero: { textAlign: 'center', padding: isMobile ? '40px 0 30px' : '60px 0 40px' },
        heroBadge: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 20px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '30px', marginBottom: '24px', fontSize: '13px', fontWeight: 700, color: '#a5b4fc' },
        heroH1: { fontSize: isMobile ? '28px' : '42px', fontWeight: 900, lineHeight: 1.3, marginBottom: '16px' },
        heroH1Em: { color: '#a5b4fc', fontStyle: 'normal' },
        heroP: { fontSize: isMobile ? '14px' : '16px', color: '#64748b', lineHeight: 1.7, marginBottom: '32px' },
        heroCta: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '16px 40px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: 800, cursor: 'pointer', textDecoration: 'none', boxShadow: '0 8px 24px rgba(99,102,241,0.4)' },
        flowSection: { padding: isMobile ? '40px 0' : '60px 0' },
        sectionTitle: { fontSize: isMobile ? '22px' : '28px', fontWeight: 900, textAlign: 'center', marginBottom: '8px' },
        sectionTitleEm: { color: '#a5b4fc', fontStyle: 'normal' },
        sectionSub: { fontSize: '14px', color: '#64748b', textAlign: 'center', marginBottom: '40px' },
        flowSteps: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'flex-start', justifyContent: 'center', gap: 0 },
        flowStep: { textAlign: 'center', width: isMobile ? '100%' : '160px', maxWidth: isMobile ? '200px' : 'none', position: 'relative' },
        flowStepNum: { width: '40px', height: '40px', background: '#6366f1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 900, margin: '0 auto 12px' },
        flowStepIcon: { fontSize: '32px', marginBottom: '8px' },
        flowStepTitle: { fontSize: '14px', fontWeight: 800, marginBottom: '4px' },
        flowStepDesc: { fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 },
        flowArrow: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: '#475569', padding: isMobile ? '8px 0' : '16px 0 0', transform: isMobile ? 'rotate(90deg)' : 'none' },
        simSection: { padding: isMobile ? '30px 0 40px' : '40px 0 60px' },
        simGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' },
        simCard: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'hidden' },
        simCardHeader: { padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: '8px' },
        simCardBadge: { padding: '3px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: 700 },
        simCardTitle: { fontSize: '14px', fontWeight: 700 },
        bannerLabel: { fontSize: '10px', color: '#64748b', textAlign: 'center' },
        locationSection: { padding: isMobile ? '30px 0' : '40px 0', textAlign: 'center' },
        locationBox: { background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '16px', padding: isMobile ? '24px 20px' : '32px', maxWidth: '600px', margin: '0 auto' },
        locationIcon: { fontSize: '48px', marginBottom: '12px' },
        locationTitle: { fontSize: '20px', fontWeight: 900, marginBottom: '8px' },
        locationTitleEm: { color: '#a5b4fc', fontStyle: 'normal' },
        locationDesc: { fontSize: '13px', color: '#94a3b8', lineHeight: 1.7 },
        locationExample: { display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' },
        locationTag: { padding: '6px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', fontSize: '11px', color: '#c7d2fe' },
        locationTagEm: { color: '#a5b4fc', fontStyle: 'normal', fontWeight: 700 },
        featuresSection: { padding: isMobile ? '40px 0' : '60px 0' },
        featureGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: '14px' },
        featureCard: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', padding: '22px 18px', textAlign: 'center' },
        featureIcon: { fontSize: '30px', marginBottom: '10px' },
        featureTitle: { fontSize: '14px', fontWeight: 800, marginBottom: '4px' },
        featureDesc: { fontSize: '11px', color: '#94a3b8', lineHeight: 1.5 },
        ctaSection: { padding: isMobile ? '40px 0' : '60px 0', textAlign: 'center' },
        ctaBox: { background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(99,102,241,0.05))', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '20px', padding: isMobile ? '28px 20px' : '40px' },
        ctaLabel: { fontSize: '14px', color: '#a5b4fc', marginBottom: '8px' },
        ctaTitle: { fontSize: isMobile ? '32px' : '40px', fontWeight: 900, color: '#a5b4fc', marginBottom: '8px' },
        ctaDesc: { fontSize: '13px', color: '#64748b', marginBottom: '24px' },
        ctaTags: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', marginBottom: '24px' },
        ctaTag: { display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', fontSize: '12px', fontWeight: 600, color: '#e2e8f0' },
        ctaBtn: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '16px 40px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '14px', fontSize: '17px', fontWeight: 800, cursor: 'pointer', textDecoration: 'none', boxShadow: '0 8px 24px rgba(99,102,241,0.3)' },
        ctaContact: { fontSize: '11px', color: '#475569', marginTop: '12px' },
        footer: { textAlign: 'center', padding: '24px 0', borderTop: '1px solid rgba(255,255,255,0.06)', color: '#475569', fontSize: '12px' },
    };

    const flowSteps = [
        { num: 1, icon: '📝', title: '모집 신청', desc: '기공소명·전화·\n이메일만 입력' },
        { num: 2, icon: '💳', title: '유료 결제', desc: '월 10만원\n회원가입 후 결제' },
        { num: 3, icon: '📸', title: '단가·사진 업로드', desc: '단가표 + 작업물\n사진 5장' },
        { num: 4, icon: '🚀', title: '마케팅 시작', desc: '덴탈아리랑 배너\n+ 채널 순차 오픈' },
        { num: 5, icon: '🏥', title: '치과 유입', desc: '치과가 우리 기공소를\n검색해서 의뢰' },
    ];

    // 8개 통합 마케팅 채널 (status: 'live' = 진행중, 'upcoming' = 순차 오픈 예정)
    const channels = [
        { icon: '📰', title: '덴탈아리랑 배너',    desc: '기공계 전문매체\n365일 배너 노출',  status: 'live' },
        { icon: '🏥', title: '치과 직접 방문',     desc: '영업사원이\n치과 직접 방문',        status: 'upcoming' },
        { icon: '📰', title: '치의신보 배너',      desc: '치과 전문매체\n순차 오픈 예정',     status: 'upcoming' },
        { icon: '📷', title: '덴트포토 배너',      desc: '디지털 치과\n순차 오픈 예정',       status: 'upcoming' },
        { icon: '🎪', title: '전시회·박람회 부스', desc: 'SIDEX·KIDC 등\n순차 오픈 예정',     status: 'upcoming' },
        { icon: '📸', title: '인스타그램',         desc: '작업물·인터뷰\n순차 오픈 예정',     status: 'upcoming' },
        { icon: '📺', title: '유튜브',             desc: '기공소 탐방\n순차 오픈 예정',       status: 'upcoming' },
        { icon: '✏️', title: '네이버 블로그',      desc: 'SEO 최적화\n순차 오픈 예정',         status: 'upcoming' },
    ];

    const features = [
        { icon: '📋', title: '디지털 의뢰서', desc: 'PDF 의뢰서 자동 인식\n온라인 작성 및 전송' },
        { icon: '💬', title: '실시간 채팅', desc: '치과와 실시간 소통\n파일·사진 전송 가능' },
        { icon: '📄', title: '거래명세서 자동 생성', desc: '거래 내역 기반 자동 생성\nPDF 발행 및 전송' },
        { icon: '📊', title: '매출 · 통계 분석', desc: '매출 추이, 거래처별 분석\n리메이크율 관리' },
        { icon: '📸', title: '포트폴리오 게시', desc: '작업물 사진 업로드\n치과에 실력 어필' },
        { icon: '📅', title: '일정 · 납기 관리', desc: '캘린더로 납기일 관리\n알림으로 놓치지 않게' },
    ];

    return (
        <div style={S.body}>
            <div style={S.header}>
                <div style={S.headerInner}>
                    <div style={S.logoRow}>
                        <div style={S.logoIcon}>DC</div>
                        <span style={S.logoText}>덴탈클러스터</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <a href={findLabUrl} style={S.headerBtnSecondary}>모집된 기공소 확인</a>
                        <button onClick={goSignup} style={S.headerBtn}>모집 참여하기 →</button>
                    </div>
                </div>
            </div>

            <div style={S.container}>
                {/* 📢 안내장 — 소장님 인사말 + 배너 일정 (최상단) */}
                <div style={{ padding: isMobile ? '20px 0 0' : '28px 0 0' }}>
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(245,196,81,0.06))',
                        border: '1.5px solid rgba(165,180,252,0.25)',
                        borderRadius: 18,
                        padding: isMobile ? '24px 20px' : '32px 36px',
                        position: 'relative',
                    }}>
                        {/* 헤더 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '5px 12px', background: '#6366f1', color: '#fff',
                                borderRadius: 16, fontSize: 11, fontWeight: 800,
                            }}>📢 안내</div>
                            <h2 style={{
                                fontSize: isMobile ? 17 : 19, fontWeight: 800, color: '#fff',
                                margin: 0, letterSpacing: '-0.3px',
                            }}>
                                덴탈클러스터를 만들게 된 이유
                            </h2>
                        </div>

                        {/* 본문 */}
                        <div style={{
                            fontSize: isMobile ? 13 : 14, color: '#cbd5e1',
                            lineHeight: 1.85, marginBottom: 18,
                        }}>
                            <p style={{ margin: '0 0 12px' }}>
                                <strong style={{ color: '#fff' }}>영업에 어려움이 있는 한 기공사로써, 10년 동안 기공소를 운영해온 한 소장으로써,</strong> 흩어져 의뢰서를 받고 거래명세서를 관리하는데 불편함이 많았던 부분을 최소한으로 해소하였습니다.
                            </p>
                            <p style={{ margin: '0 0 12px' }}>
                                혼자서 영업하면 한 달에 <strong style={{ color: '#fbbf24' }}>100만원 이상</strong>이 나갑니다. 함께하여 <strong style={{ color: '#a5b4fc' }}>월 10만원</strong>으로 치과 배너 · 전시회 부스 · 방문 영업 · 인스타 · 블로그 마케팅을 통해서 정식적으로 개인 기공소를 치과에 알리고자 하는 마음으로 만들었습니다.
                            </p>
                            <p style={{ margin: 0, color: '#fff', fontWeight: 600 }}>
                                소장님들의 많은 관심과 참여 부탁드립니다.
                            </p>
                        </div>

                        {/* 배너 일정 박스 */}
                        <div style={{
                            background: 'rgba(0,0,0,0.25)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: 12,
                            padding: isMobile ? '14px 16px' : '18px 22px',
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: isMobile ? 12 : 16,
                        }}>
                            <div>
                                <div style={{
                                    display: 'inline-block',
                                    padding: '3px 10px',
                                    background: 'rgba(245,158,11,0.15)',
                                    color: '#fbbf24',
                                    borderRadius: 10,
                                    fontSize: 10,
                                    fontWeight: 800,
                                    marginBottom: 6,
                                }}>현재 진행 중</div>
                                <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 3 }}>
                                    덴탈2804 배너
                                </div>
                                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                                    2026년 <strong style={{ color: '#fff' }}>5월 말까지</strong> 게시
                                </div>
                            </div>
                            <div style={{ borderLeft: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)', borderTop: isMobile ? '1px solid rgba(255,255,255,0.08)' : 'none', paddingLeft: isMobile ? 0 : 16, paddingTop: isMobile ? 12 : 0 }}>
                                <div style={{
                                    display: 'inline-block',
                                    padding: '3px 10px',
                                    background: 'rgba(16,185,129,0.15)',
                                    color: '#34d399',
                                    borderRadius: 10,
                                    fontSize: 10,
                                    fontWeight: 800,
                                    marginBottom: 6,
                                }}>다음 단계</div>
                                <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 3 }}>
                                    덴탈아리랑 배너
                                </div>
                                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                                    2026년 <strong style={{ color: '#fff' }}>6월부터</strong> 게재 시작
                                </div>
                            </div>
                        </div>

                        <p style={{ fontSize: 12, color: '#64748b', textAlign: 'right', margin: '14px 0 0' }}>
                            — 덴탈클러스터 드림. 감사합니다.
                        </p>
                    </div>
                </div>

                {/* 히어로 */}
                <div style={S.hero}>
                    <div style={S.heroBadge}>🚀 기공소 모집중</div>
                    <h1 style={S.heroH1}>
                        덴탈클러스터가 직접<br />
                        <em style={S.heroH1Em}>치과로 찾아갑니다</em>
                    </h1>
                    <p style={S.heroP}>
                        배너 광고 · 전시회 부스 · 인스타 · 유튜브 · 블로그까지<br />
                        <strong style={{ color: '#fff' }}>영업·마케팅 전부를</strong> 덴탈클러스터가 대신합니다.
                    </p>
                    <div style={S.heroCtaRow}>
                        <button onClick={goSignup} style={S.heroCta}>모집 참여하기 →</button>
                        <a href={findLabUrl} style={S.heroCtaSecondary}>모집된 기공소 확인</a>
                    </div>
                    <p style={{ fontSize: isMobile ? '12px' : '13px', color: '#94a3b8', margin: '16px 0 0', lineHeight: 1.6 }}>
                        📞 전화 상담 010-2972-3733 · 카카오톡 @덴탈클러스터
                    </p>
                    <p style={{ fontSize: '11px', color: '#10b981', margin: '8px 0 0', lineHeight: 1.5, fontWeight: 600 }}>
                        ✓ 덴탈아리랑 배너 광고 진행 중 · 다른 채널 순차 오픈 예정
                    </p>
                    <p style={{ fontSize: '11px', color: '#fbbf24', margin: '4px 0 0', lineHeight: 1.5 }}>
                        ※ 배너 광고는 회원가입 후 유료 결제 시 제공됩니다
                    </p>
                </div>

                {/* 8개 통합 마케팅 채널 */}
                <div style={{ padding: isMobile ? '40px 0 20px' : '60px 0 30px' }}>
                    <div style={S.sectionTitle}>이 모든 걸 <em style={S.sectionTitleEm}>덴탈클러스터가</em></div>
                    <div style={S.sectionSub}>월 10만원에 8개 채널 통합 마케팅 · <span style={{ color: '#fbbf24' }}>단계적 오픈 예정</span></div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '12px', maxWidth: '900px', margin: '0 auto' }}>
                        {channels.map((c, i) => {
                            const isLive = c.status === 'live';
                            return (
                                <div key={i} style={{
                                    background: isLive ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.04)',
                                    border: isLive ? '1.5px solid rgba(16,185,129,0.4)' : '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: '14px',
                                    padding: isMobile ? '18px 14px' : '22px 16px',
                                    textAlign: 'center',
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        position: 'absolute', top: 8, right: 8,
                                        padding: '2px 8px', borderRadius: 12,
                                        fontSize: 9, fontWeight: 800,
                                        background: isLive ? '#10b981' : 'rgba(255,255,255,0.08)',
                                        color: isLive ? '#fff' : '#94a3b8',
                                        border: isLive ? 'none' : '1px solid rgba(255,255,255,0.12)'
                                    }}>
                                        {isLive ? '진행 중' : '오픈 예정'}
                                    </div>
                                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>{c.icon}</div>
                                    <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '6px', color: '#fff' }}>{c.title}</div>
                                    <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.5, whiteSpace: 'pre-line' }}>{c.desc}</div>
                                </div>
                            );
                        })}
                    </div>
                    <p style={{ textAlign: 'center', fontSize: isMobile ? '13px' : '14px', color: '#a5b4fc', margin: '24px auto 0', maxWidth: '600px', lineHeight: 1.7, fontWeight: 600 }}>
                        영업사원 채용도, 광고대행사 별도 계약도 필요 없습니다.<br />
                        덴탈클러스터 하나로 모든 채널이 한 번에.
                    </p>
                    <p style={{ textAlign: 'center', fontSize: '11px', color: '#64748b', margin: '12px auto 0', maxWidth: '600px', lineHeight: 1.6 }}>
                        ※ 8개 채널은 모집 진행에 맞춰 순차 오픈됩니다. 모집 시점부터 가입 기공소에 동일하게 적용되며, 채널 오픈 일정은 별도 안내합니다.
                    </p>
                </div>

                {/* 흐름도 */}
                <div style={S.flowSection}>
                    <div style={S.sectionTitle}>이렇게 <em style={S.sectionTitleEm}>홍보</em>됩니다</div>
                    <div style={S.sectionSub}>가입부터 치과 유입까지 4단계</div>
                    <div style={S.flowSteps}>
                        {flowSteps.map((step, idx) => (
                            <React.Fragment key={step.num}>
                                <div style={S.flowStep}>
                                    <div style={S.flowStepNum}>{step.num}</div>
                                    <div style={S.flowStepIcon}>{step.icon}</div>
                                    <div style={S.flowStepTitle}>{step.title}</div>
                                    <div style={{ ...S.flowStepDesc, whiteSpace: 'pre-line' }}>{step.desc}</div>
                                </div>
                                {idx < flowSteps.length - 1 && <div style={S.flowArrow}>→</div>}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* ✍️ 소장님은 최소한만, 나머지는 저희가 */}
                <div style={{ padding: isMobile ? '30px 0 20px' : '40px 0 30px' }}>
                    <div style={S.sectionTitle}>소장님은 <em style={S.sectionTitleEm}>두 가지</em>만 올려주세요</div>
                    <div style={S.sectionSub}>나머지 홍보 콘텐츠는 덴탈클러스터가 기공소 특성에 맞게 작성합니다</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '14px' : '20px', maxWidth: '780px', margin: '0 auto' }}>
                        {/* 좌: 소장님이 해야 할 것 */}
                        <div style={{ background: 'rgba(245,196,81,0.06)', border: '1px solid rgba(245,196,81,0.25)', borderRadius: '16px', padding: isMobile ? '20px 18px' : '24px 22px' }}>
                            <div style={{ display: 'inline-block', padding: '4px 12px', background: '#f5c451', color: '#0f172a', borderRadius: '20px', fontSize: '11px', fontWeight: 800, marginBottom: '14px' }}>소장님이 올릴 것</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px' }}>
                                    <span style={{ fontSize: '22px' }}>💰</span>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#fff' }}>단가표</div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>품목별 가격·납기일 입력</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px' }}>
                                    <span style={{ fontSize: '22px' }}>📸</span>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#fff' }}>포트폴리오 사진</div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>작업물·스튜디오 사진 업로드</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 우: 덴탈클러스터가 대신 */}
                        <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '16px', padding: isMobile ? '20px 18px' : '24px 22px' }}>
                            <div style={{ display: 'inline-block', padding: '4px 12px', background: '#6366f1', color: '#fff', borderRadius: '20px', fontSize: '11px', fontWeight: 800, marginBottom: '14px' }}>덴탈클러스터가 대신</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {[
                                    '치과 직접 방문 영업',
                                    '치의신보·덴탈아리랑·덴트포토 배너',
                                    '전시회·박람회 부스 운영',
                                    '인스타그램 콘텐츠 제작·운영',
                                    '유튜브 영상 촬영·편집·게시',
                                    '네이버 블로그 글 작성·SEO',
                                    '기공소 소개글·강점 카피 작성',
                                    '치과 매체·SNS 홍보 콘텐츠 제작',
                                ].map((item, idx) => (
                                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#e2e8f0' }}>
                                        <span style={{ color: '#a5b4fc', fontWeight: 900 }}>✓</span>
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <p style={{ textAlign: 'center', fontSize: isMobile ? '12px' : '13px', color: '#94a3b8', margin: '20px auto 0', maxWidth: '560px', lineHeight: 1.6 }}>
                        소장님은 기공 작업에만 집중하세요 — 홍보 콘텐츠 제작은 덴탈클러스터 전담팀이 담당합니다.
                    </p>
                </div>

                {/* 🔒 단가표 보안 섹션 */}
                <div style={{ padding: isMobile ? '30px 0' : '40px 0' }}>
                    <div style={{ maxWidth: '720px', margin: '0 auto', background: 'rgba(16,185,129,0.06)', border: '1.5px solid rgba(16,185,129,0.25)', borderRadius: '18px', padding: isMobile ? '24px 20px' : '32px 36px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '32px' }}>🔒</span>
                            <h3 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 900, color: '#fff', margin: 0, textAlign: 'center' }}>
                                단가표는 <span style={{ color: '#10b981' }}>치과에만 공개</span>됩니다
                            </h3>
                        </div>
                        <p style={{ fontSize: isMobile ? '13px' : '14px', color: '#94a3b8', lineHeight: 1.75, textAlign: 'center', margin: '0 0 18px' }}>
                            경쟁 기공소 간 단가 노출 걱정 없습니다. <strong style={{ color: '#fff' }}>로그인한 치과 회원만 단가·연락처를 열람</strong>할 수 있고,<br />
                            <strong style={{ color: '#f5c451' }}>다른 기공소는 서로의 단가표를 확인할 수 없습니다.</strong>
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '10px', marginTop: '16px' }}>
                            <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', textAlign: 'center' }}>
                                <div style={{ fontSize: '20px', marginBottom: '4px' }}>🏥</div>
                                <div style={{ fontSize: '12px', fontWeight: 800, color: '#10b981' }}>치과 회원</div>
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>단가·연락처 전체 열람</div>
                            </div>
                            <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', textAlign: 'center' }}>
                                <div style={{ fontSize: '20px', marginBottom: '4px' }}>🔧</div>
                                <div style={{ fontSize: '12px', fontWeight: 800, color: '#f5c451' }}>타 기공소</div>
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>단가·연락처 블러 처리</div>
                            </div>
                            <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', textAlign: 'center' }}>
                                <div style={{ fontSize: '20px', marginBottom: '4px' }}>👤</div>
                                <div style={{ fontSize: '12px', fontWeight: 800, color: '#94a3b8' }}>비회원</div>
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>이름·사진·단가 전부 비공개</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 시뮬레이션 */}
                <div style={S.simSection}>
                    <div style={S.sectionTitle}>치과에서 이렇게 <em style={S.sectionTitleEm}>보입니다</em></div>
                    <div style={S.sectionSub}>실제 치과가 보는 화면입니다</div>

                    <div style={S.simGrid}>
                        {/* 치의신보 시뮬 (오픈 예정) */}
                        <div style={{ ...S.simCard, opacity: 0.7 }}>
                            <div style={S.simCardHeader}>
                                <div style={{ ...S.simCardBadge, background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>치의신보</div>
                                <div style={S.simCardTitle}>dailydental.co.kr</div>
                                <div style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 10, fontSize: 9, fontWeight: 800, background: 'rgba(255,255,255,0.08)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.12)' }}>오픈 예정</div>
                            </div>
                            <div style={{ padding: 0, background: '#fff', color: '#000' }}>
                                <div style={{ padding: '5px 10px', background: '#f1f1f1', display: 'flex', alignItems: 'center', gap: '6px', borderBottom: '1px solid #ddd' }}>
                                    <div style={{ display: 'flex', gap: '3px' }}>
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff5f57' }} />
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ffbd2e' }} />
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#28c840' }} />
                                    </div>
                                    <div style={{ flex: 1, padding: '3px 8px', background: '#fff', border: '1px solid #ddd', borderRadius: '4px', fontSize: '7px', color: '#333' }}>dailydental.co.kr</div>
                                </div>
                                <div style={{ padding: '8px', position: 'relative' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0 6px', fontSize: '6px', color: '#999', borderBottom: '1px solid #eee', marginBottom: '6px' }}>
                                        <span>2026.04.20 (일)</span>
                                        <div style={{ display: 'flex', gap: '6px' }}><span>기사제보</span><span>광고문의</span><span>로그인</span></div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                        <div style={{ background: '#eee', borderRadius: '3px', padding: '4px 10px', fontSize: '6px', color: '#999' }}>광고</div>
                                        <div style={{ fontSize: '16px', fontWeight: 900, color: '#1a1a1a', letterSpacing: '2px' }}>치의신보</div>
                                        <div style={{ background: '#eee', borderRadius: '3px', padding: '4px 10px', fontSize: '6px', color: '#999' }}>광고</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', borderTop: '2px solid #333', borderBottom: '1px solid #eee', marginBottom: '8px', fontSize: '7px', fontWeight: 700, color: '#333' }}>
                                        <span>정책</span><span>기획</span><span>학술</span><span>비즈니스</span><span>사설/칼럼</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <div style={{ width: '50px', flexShrink: 0 }}>
                                            <div style={{ background: '#eee', borderRadius: '3px', height: '60px', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5px', color: '#bbb' }}>광고</div>
                                            <div style={{ background: '#eee', borderRadius: '3px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5px', color: '#bbb' }}>광고</div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                                                <div style={{ flex: 1, background: '#e8e8e8', borderRadius: '3px', height: '55px', display: 'flex', alignItems: 'flex-end', padding: '4px' }}>
                                                    <div style={{ fontSize: '6px', fontWeight: 700, color: '#555' }}>치과 관련 기사 제목...</div>
                                                </div>
                                                <div style={{ flex: 1, background: '#e0e0e0', borderRadius: '3px', height: '55px', display: 'flex', alignItems: 'flex-end', padding: '4px' }}>
                                                    <div style={{ fontSize: '6px', fontWeight: 700, color: '#555' }}>업계 동향 기사...</div>
                                                </div>
                                            </div>
                                            <div style={{ fontSize: '6px', color: '#999', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <div style={{ padding: '2px 0', borderBottom: '1px solid #f5f5f5' }}>• 최신 뉴스 기사 제목...</div>
                                                <div style={{ padding: '2px 0', borderBottom: '1px solid #f5f5f5' }}>• 학술 관련 기사...</div>
                                                <div style={{ padding: '2px 0' }}>• 정책 뉴스...</div>
                                            </div>
                                        </div>
                                        <div style={{ width: '70px', flexShrink: 0 }}>
                                            <div style={{ background: 'linear-gradient(170deg,#0f1b2d,#1a3a5c)', borderRadius: '4px', padding: '6px', marginBottom: '4px', border: '2px solid #6366f1', position: 'relative' }}>
                                                <div style={{ position: 'absolute', top: '-5px', left: '50%', transform: 'translateX(-50%)', background: '#6366f1', color: '#fff', fontSize: '5px', fontWeight: 800, padding: '1px 5px', borderRadius: '3px', whiteSpace: 'nowrap' }}>⬅ 우리 배너</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '3px', marginTop: '4px' }}>
                                                    <div style={{ width: '8px', height: '8px', background: '#6366f1', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4px', fontWeight: 900, color: '#fff' }}>DC</div>
                                                </div>
                                                <div style={{ fontSize: '7px', fontWeight: 800, color: '#fff', lineHeight: 1.3, marginBottom: '3px' }}><span style={{ color: '#a5b4fc' }}>기공소</span><br />찾아보기</div>
                                                <div style={{ fontSize: '5px', padding: '2px 4px', background: '#6366f1', borderRadius: '2px', textAlign: 'center', fontWeight: 700, color: '#fff' }}>확인하기</div>
                                            </div>
                                            <div style={{ background: '#eee', borderRadius: '3px', height: '40px', marginBottom: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5px', color: '#bbb' }}>광고</div>
                                            <div style={{ background: '#eee', borderRadius: '3px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5px', color: '#bbb' }}>광고</div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ ...S.bannerLabel, padding: '8px' }}>↑ 치의신보 우측 상단에 365일 배너 노출</div>
                            </div>
                        </div>

                        {/* 덴탈아리랑 시뮬 (진행 중) */}
                        <div style={{ ...S.simCard, border: '1.5px solid rgba(16,185,129,0.4)', boxShadow: '0 0 0 1px rgba(16,185,129,0.15)' }}>
                            <div style={S.simCardHeader}>
                                <div style={{ ...S.simCardBadge, background: 'rgba(16,185,129,0.15)', color: '#34d399' }}>덴탈아리랑</div>
                                <div style={S.simCardTitle}>dentalariang.com</div>
                                <div style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 10, fontSize: 9, fontWeight: 800, background: '#10b981', color: '#fff' }}>진행 중</div>
                            </div>
                            <div style={{ padding: 0, background: '#fff', color: '#000' }}>
                                <div style={{ padding: '5px 10px', background: '#f1f1f1', display: 'flex', alignItems: 'center', gap: '6px', borderBottom: '1px solid #ddd' }}>
                                    <div style={{ display: 'flex', gap: '3px' }}>
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff5f57' }} />
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ffbd2e' }} />
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#28c840' }} />
                                    </div>
                                    <div style={{ flex: 1, padding: '3px 8px', background: '#fff', border: '1px solid #ddd', borderRadius: '4px', fontSize: '7px', color: '#333' }}>dentalariang.com</div>
                                </div>
                                <div style={{ padding: '8px', position: 'relative' }}>
                                    <div style={{ textAlign: 'center', padding: '6px 0', borderBottom: '1px solid #eee', marginBottom: '6px' }}>
                                        <div style={{ fontSize: '14px', fontWeight: 900, color: '#2d8f3e' }}>덴탈아리랑</div>
                                        <div style={{ fontSize: '6px', color: '#999' }}>치과기공 전문 미디어</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', padding: '4px 0', marginBottom: '8px', fontSize: '7px', fontWeight: 600, color: '#555', borderBottom: '1px solid #eee' }}>
                                        <span>뉴스</span><span>임상</span><span>경영</span><span>세미나</span><span>자유게시판</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <div style={{ width: '55px', flexShrink: 0 }}>
                                            <div style={{ background: '#eee', borderRadius: '3px', height: '65px', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5px', color: '#bbb' }}>광고</div>
                                            <div style={{ background: '#eee', borderRadius: '3px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5px', color: '#bbb' }}>광고</div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ background: '#e8e8e8', borderRadius: '3px', height: '60px', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '6px', color: '#999' }}>메인 배너 슬라이드</div>
                                            <div style={{ fontSize: '6px', color: '#999', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <div style={{ padding: '2px 0', borderBottom: '1px solid #f5f5f5' }}>• 치과기공 뉴스...</div>
                                                <div style={{ padding: '2px 0', borderBottom: '1px solid #f5f5f5' }}>• 신제품 정보...</div>
                                                <div style={{ padding: '2px 0' }}>• 세미나 일정...</div>
                                            </div>
                                        </div>
                                        <div style={{ width: '70px', flexShrink: 0 }}>
                                            <div style={{ background: 'linear-gradient(170deg,#1e1b4b,#312e81)', borderRadius: '4px', padding: '6px', marginBottom: '4px', border: '2px solid #6366f1', position: 'relative' }}>
                                                <div style={{ position: 'absolute', top: '-5px', left: '50%', transform: 'translateX(-50%)', background: '#6366f1', color: '#fff', fontSize: '5px', fontWeight: 800, padding: '1px 5px', borderRadius: '3px', whiteSpace: 'nowrap' }}>⬅ 우리 배너</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '3px', marginTop: '4px' }}>
                                                    <div style={{ width: '8px', height: '8px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4px', fontWeight: 900, color: '#fff' }}>DC</div>
                                                </div>
                                                <div style={{ fontSize: '7px', fontWeight: 800, color: '#fff', lineHeight: 1.3, marginBottom: '3px' }}>우리 근처<br /><span style={{ color: '#a5b4fc' }}>기공소</span><br />찾기</div>
                                                <div style={{ fontSize: '5px', padding: '2px 4px', background: '#6366f1', borderRadius: '2px', textAlign: 'center', fontWeight: 700, color: '#fff' }}>찾기 →</div>
                                            </div>
                                            <div style={{ background: '#eee', borderRadius: '3px', height: '40px', marginBottom: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5px', color: '#bbb' }}>광고</div>
                                            <div style={{ background: '#eee', borderRadius: '3px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '5px', color: '#bbb' }}>광고</div>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ ...S.bannerLabel, padding: '8px' }}>↑ 덴탈아리랑 우측 상단에 365일 배너 노출</div>
                            </div>
                        </div>
                    </div>

                    {/* 클릭 후 화면 */}
                    <div style={{ textAlign: 'center', marginTop: '24px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '28px' }}>👆</div>
                        <div style={{ fontSize: '15px', color: '#a5b4fc', fontWeight: 800, marginBottom: '4px' }}>치과가 배너를 클릭하면?</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>이 화면이 열립니다 — 위치 기반으로 가까운 기공소 순서대로 노출</div>
                    </div>

                    <div style={{ maxWidth: '700px', margin: '0 auto', borderRadius: '14px', overflow: 'hidden', border: '2px solid rgba(99,102,241,0.3)', boxShadow: '0 8px 30px rgba(99,102,241,0.15)' }}>
                        <img src="/images/promo/find-lab-screenshot.jpg" alt="기공소 찾기 화면" style={{ width: '100%', display: 'block' }} />
                    </div>
                    <div style={{ ...S.bannerLabel, marginTop: '10px', fontSize: '12px' }}>↑ 가까운 순서대로 우리 기공소가 치과에 노출됩니다</div>
                </div>

                {/* 위치 기반 설명 */}
                <div style={S.locationSection}>
                    <div style={S.locationBox}>
                        <div style={S.locationIcon}>📍</div>
                        <div style={S.locationTitle}><em style={S.locationTitleEm}>위치 기반</em>으로 가까운 치과에 노출</div>
                        <div style={S.locationDesc}>
                            치과가 기공소를 검색하면 GPS 위치를 기반으로<br />
                            <strong style={{ color: '#a5b4fc' }}>가까운 기공소가 1순위</strong>로 노출됩니다.<br />
                            강남 치과가 검색하면 강남 근처 기공소가 먼저 보입니다.
                        </div>
                        <div style={S.locationExample}>
                            <div style={S.locationTag}>강남 치과 → <em style={S.locationTagEm}>강남 기공소 1순위</em></div>
                            <div style={S.locationTag}>김포 치과 → <em style={S.locationTagEm}>김포 기공소 1순위</em></div>
                        </div>
                    </div>
                </div>

                {/* 기능 */}
                <div style={S.featuresSection}>
                    <div style={S.sectionTitle}>구독 하나로 <em style={S.sectionTitleEm}>이 모든 걸</em></div>
                    <div style={S.sectionSub}>기공소 운영에 필요한 모든 기능을 제공합니다</div>
                    <div style={S.featureGrid}>
                        {features.map((f, i) => (
                            <div key={i} style={S.featureCard}>
                                <div style={S.featureIcon}>{f.icon}</div>
                                <div style={S.featureTitle}>{f.title}</div>
                                <div style={{ ...S.featureDesc, whiteSpace: 'pre-line' }}>{f.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA */}
                <div style={S.ctaSection}>
                    <div style={S.ctaBox}>
                        <div style={S.ctaLabel}>✦ 기공소 모집중</div>
                        <div style={S.ctaTitle}>월 10만원</div>
                        <div style={S.ctaDesc}>덴탈클러스터의 통합 마케팅을 월 10만원에<br />부가세 별도 · 약정 없음 · 언제든 해지<br /><span style={{ color: '#10b981', fontWeight: 700 }}>✓ 덴탈아리랑 배너 진행 중</span> <span style={{ color: '#fbbf24', fontWeight: 600 }}>· 다른 채널 순차 오픈 예정</span></div>
                        <div style={S.ctaTags}>
                            <span style={{ ...S.ctaTag, background: 'rgba(16,185,129,0.15)', borderColor: 'rgba(16,185,129,0.4)', color: '#34d399' }}>✓ 덴탈아리랑 배너 (진행 중)</span>
                            <span style={S.ctaTag}>🔜 치의신보·덴트포토</span>
                            <span style={S.ctaTag}>🔜 치과 직접 방문</span>
                            <span style={S.ctaTag}>🔜 전시회 부스</span>
                            <span style={S.ctaTag}>🔜 인스타·유튜브·블로그</span>
                            <span style={S.ctaTag}>✓ 주문·채팅·거래명세서</span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button onClick={goSignup} style={S.ctaBtn}>모집 참여하기 →</button>
                            <a href={findLabUrl} style={S.ctaBtnSecondary}>모집된 기공소 확인</a>
                        </div>
                        <div style={S.ctaContact}>문의: 010-2972-3733 · 카카오톡 @덴탈클러스터</div>
                    </div>
                </div>
            </div>

            <div style={S.footer}>
                <p>덴탈클러스터 (DentalCluster) · www.dentalcluster.com</p>
                <p>© 2026 DentalCluster. All rights reserved.</p>
            </div>

            {/* 모집 신청 모달 */}
            {showModal && (
                <div
                    onClick={closeModal}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{ background: '#fff', borderRadius: 20, padding: isMobile ? '24px 20px' : '32px 28px', maxWidth: 440, width: '100%', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.4)', color: '#0f172a' }}
                    >
                        <button
                            onClick={closeModal}
                            style={{ position: 'absolute', top: 14, right: 14, width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, color: '#64748b' }}
                        >✕</button>

                        {submitted ? (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#16a34a', fontSize: 30, fontWeight: 900 }}>✓</div>
                                <h3 style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginBottom: 10, margin: '0 0 10px' }}>신청이 완료되었습니다</h3>
                                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.7, marginBottom: 24, margin: '0 0 24px' }}>
                                    등록된 연락처로 관리자가 직접 연락드려<br />
                                    참여 조건과 일정을 안내해 드립니다.
                                </p>
                                <button
                                    onClick={closeModal}
                                    style={{ width: '100%', padding: 14, background: '#0f172a', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}
                                >확인</button>
                            </div>
                        ) : (
                            <>
                                <h3 style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginBottom: 6, letterSpacing: '-0.3px', margin: '0 0 6px' }}>모집 신청하기</h3>
                                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65, marginBottom: 22, margin: '0 0 22px' }}>
                                    아래 정보를 남겨주시면 관리자가 직접 연락드려<br />
                                    <strong style={{ color: '#0f172a' }}>참여 조건과 일정</strong>을 안내해 드립니다.
                                </p>

                                {errorMsg && (
                                    <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 12, marginBottom: 14 }}>{errorMsg}</div>
                                )}

                                <div style={{ marginBottom: 14 }}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6, display: 'block' }}>🏭 기공소 이름</label>
                                    <input
                                        type="text"
                                        value={form.labName}
                                        onChange={e => setForm({ ...form, labName: e.target.value })}
                                        placeholder="예: 행복치과기공소"
                                        style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box', background: '#fafbfc' }}
                                    />
                                </div>
                                <div style={{ marginBottom: 14 }}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6, display: 'block' }}>📞 전화번호</label>
                                    <input
                                        type="tel"
                                        value={form.phone}
                                        onChange={e => setForm({ ...form, phone: e.target.value })}
                                        placeholder="예: 010-1234-5678"
                                        style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box', background: '#fafbfc' }}
                                    />
                                </div>
                                <div style={{ marginBottom: 22 }}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6, display: 'block' }}>✉ 이메일</label>
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={e => setForm({ ...form, email: e.target.value })}
                                        placeholder="example@email.com"
                                        style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box', background: '#fafbfc' }}
                                    />
                                </div>

                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    style={{ width: '100%', padding: 14, background: '#6366f1', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1, boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }}
                                >{submitting ? '발송 중...' : '📨 발송하기'}</button>
                                <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 12, lineHeight: 1.5 }}>
                                    입력하신 정보는 관리자 확인 용도로만 사용됩니다.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default LabRecruitLanding;
